import { Controller } from '@hotwired/stimulus'
import AttachmentManagerController from './attachment-manager'

export default class FormChangeController extends Controller<HTMLFormElement> {
  declare disabledValue: boolean
  declare initializedValue: boolean
  declare modifiedValue: boolean
  declare submittedValue: boolean
  declare readonly attachmentManagerOutlets: AttachmentManagerController[]
  declare readonly buttonTargets: HTMLButtonElement[]
  declare readonly hasAttachmentManagerOutlet: boolean

  // Complex data, so not part of the controller's values
  declare initialState: FormData

  static outlets = ['attachment-manager']
  static targets = ['button']
  static values = { disabled: Boolean, initialized: Boolean, modified: Boolean, submitted: Boolean }

  // Getters

  get submitButtons() {
    return this.buttonTargets.filter(button => button.type === 'submit')
  }

  // Submit buttons that provide alternate text to display during form submit
  get submitsWithButtons() {
    return this.submitButtons.filter(button => Boolean(button.dataset.submitsWith))
  }

  get isValid() {
    return this.element.checkValidity()
  }

  // Lifecycle methods

  connect() {
    // Defer capturing initial state so other controllers (e.g. TomSelect)
    // finish initializing and modifying form elements first.
    requestAnimationFrame(() => {
      this.initializedValue = true
      this.initialState = this.#createSnapshot()
    })

    this.element.addEventListener('cocoon:after-insert', this.#handleUntraceableChange)
    this.element.addEventListener('cocoon:after-remove', this.#handleUntraceableChange)
    this.element.addEventListener('input', this.#handleInput)
    this.element.addEventListener('reset', this.#resetForm)
    this.element.addEventListener('submit', this.#handleSubmit)
  }

  disconnect() {
    this.element.removeEventListener('cocoon:after-insert', this.#handleUntraceableChange)
    this.element.removeEventListener('cocoon:after-remove', this.#handleUntraceableChange)
    this.element.removeEventListener('input', this.#handleInput)
    this.element.removeEventListener('reset', this.#resetForm)
    this.element.removeEventListener('submit', this.#handleSubmit)
  }

  attachmentManagerOutletConnected(attachmentManager: AttachmentManagerController) {
    attachmentManager.element.addEventListener(
      'attachment-manager:busyChange',
      this.#handleAttachmentManagerOutletBusyChange,
    )
  }

  attachmentManagerOutletDisconnected(attachmentManager: AttachmentManagerController) {
    attachmentManager.element.removeEventListener(
      'attachment-manager:busyChange',
      this.#handleAttachmentManagerOutletBusyChange,
    )
  }

  // Value change events

  disabledValueChanged = this.#updateButtons
  modifiedValueChanged = this.#updateButtons
  submittedValueChanged = this.#updateButtons

  // Private methods

  #createSnapshot() {
    return new FormData(this.element)
  }

  #handleAttachmentManagerOutletBusyChange = () => {
    this.disabledValue = this.attachmentManagerOutlets.some(attachmentManager => attachmentManager.busyValue)
  }

  #handleInput = (_event: Event) => {
    this.modifiedValue = this.#isModified()
    this.#updateButtons()
  }

  #handleSubmit = () => {
    this.submittedValue = true
  }

  // NOTE: This is useful when making changes that don't impact FormData
  // (e.g. Cocoon item addition/removal)
  #handleUntraceableChange = () => {
    this.modifiedValue = true
    this.#updateButtons()
  }

  #isModified() {
    // Bail if the controller isn't fully initialized yet
    if (!this.initialState) return false

    const currState = this.#createSnapshot()
    const initialEntries = [...this.initialState.entries()].sort().toString()
    const currentEntries = [...currState.entries()].sort().toString()

    return initialEntries !== currentEntries
  }

  #resetForm = () => {
    this.disabledValue = false
    this.modifiedValue = false
    this.submittedValue = false
  }

  #updateButtons() {
    this.#updateSubmitsWithButtonText()
    this.buttonTargets.forEach(button => {
      const { formChangeBypassModified } = button.dataset
      const modified = formChangeBypassModified ? true : this.modifiedValue

      button.disabled = this.disabledValue || !modified || !this.isValid
    })
  }

  #updateSubmitsWithButtonText() {
    if (!this.submittedValue || this.submitsWithButtons.length === 0) return

    for (const button of this.submitsWithButtons) {
      button.dataset.originalText = button.innerText
      button.innerText = button.dataset.submitsWith!
    }
  }
}
